import { normalizeCitySlug, resolveLocationSlug } from "./locationRoutes";
import { getNicheIntentBySlug } from "./nicheIntentRegistry";
import { resolveCanonicalSearchIntent } from "./canonicalIntentResolver";
import { getJobCategoryBySlug } from "./jobCategoryRegistry";

export interface SeoRedirectRule {
  from: string;
  to: string;
  permanent: boolean;
  reason: string;
}

// Fixed static legacy redirect rules
const STATIC_REDIRECT_RULES: SeoRedirectRule[] = [
  {
    from: "/business-hiring-signals",
    to: "/hiring-signal-methodology",
    permanent: true,
    reason: "Redirect duplicate signals landing page to canonical methodology guide"
  },
  {
    from: "/how-to-ask-if-hiring",
    to: "/guides/how-to-ask-if-a-business-is-hiring",
    permanent: true,
    reason: "Legacy guide URL normalization to /guides/ canonical hierarchy"
  },
  {
    from: "/how-to-ask-a-business-if-theyre-hiring",
    to: "/guides/how-to-ask-if-a-business-is-hiring",
    permanent: true,
    reason: "Legacy guide synonym normalization"
  },
  {
    from: "/how-to-contact-local-businesses-professionally",
    to: "/guides/how-to-contact-local-businesses-for-a-job",
    permanent: true,
    reason: "Legacy contact guide normalization"
  },
  {
    from: "/how-to-find-your-first-job",
    to: "/first-jobs",
    permanent: true,
    reason: "Normalize legacy first job guide path to /first-jobs"
  },
  {
    from: "/how-to-find-internships-before-theyre-posted",
    to: "/local-internships",
    permanent: true,
    reason: "Normalize legacy internship route"
  },
  {
    from: "/jobs/sydney-nsw/medical-healthcare",
    to: "/jobs/sydney-nsw-au/healthcare-jobs",
    permanent: true,
    reason: "Normalize legacy Sydney location and medical category slug to canonical /jobs/sydney-nsw-au/healthcare-jobs"
  },
  {
    from: "/chicago-il-jobs",
    to: "/jobs/chicago-il",
    permanent: true,
    reason: "Normalize Chicago location route"
  },
  {
    from: "/chicago/jobs",
    to: "/jobs/chicago-il",
    permanent: true,
    reason: "Normalize Chicago route hierarchy"
  },
  {
    from: "/jobs/chigago-il",
    to: "/jobs/chicago-il",
    permanent: true,
    reason: "Correct Chicago typo in location slug"
  },
  {
    from: "/jobs-near-me",
    to: "/jobs",
    permanent: true,
    reason: "Normalize generic jobs-near-me hub to central /jobs directory"
  },
  {
    from: "/local-jobs",
    to: "/jobs/locations",
    permanent: true,
    reason: "Normalize local-jobs index to locations directory"
  },
  {
    from: "/hiring-near-me",
    to: "/businesses-hiring-near-me",
    permanent: true,
    reason: "Normalize hiring-near-me path"
  }
];

/**
 * Returns a 301 permanent redirect rule if the path or URL contains a legacy,
 * non-canonical, or structured parameter search query.
 * Guarantees single-step resolution (0 redirect chains).
 */
export function getSeoRedirect(pathnameOrUrl: string): SeoRedirectRule | null {
  if (!pathnameOrUrl) return null;

  const rawInput = pathnameOrUrl.trim();
  const hasQuery = rawInput.includes("?");

  let cleanPath = rawInput.split("?")[0].split("#")[0].toLowerCase();

  // Remove trailing slash if not root
  if (cleanPath.length > 1 && cleanPath.endsWith("/")) {
    cleanPath = cleanPath.slice(0, -1);
  }

  // 0. Query parameter structured intent check (e.g. /find-jobs?q=First%20Jobs)
  if (hasQuery && (cleanPath === "/find-jobs" || cleanPath === "/jobs" || cleanPath === "/search")) {
    try {
      const queryString = rawInput.substring(rawInput.indexOf("?"));
      const params = new URLSearchParams(queryString);
      const q = params.get("q") || params.get("query") || params.get("search");
      const loc = params.get("loc") || params.get("location") || params.get("city");
      const category = params.get("category") || params.get("cat");

      const resolvedIntent = resolveCanonicalSearchIntent(q, loc, category);
      if (resolvedIntent && resolvedIntent.isStructuredIntent && resolvedIntent.canonicalPath !== cleanPath) {
        return {
          from: rawInput,
          to: resolvedIntent.canonicalPath,
          permanent: true,
          reason: resolvedIntent.reason
        };
      }
    } catch {
      // Ignore query parsing errors
    }
  }

  // 1. Check static rules
  const staticMatch = STATIC_REDIRECT_RULES.find(r => r.from === cleanPath);
  if (staticMatch) {
    return staticMatch;
  }

  // 2. Legacy Pattern: /jobs-near-me/:location -> /jobs/:location
  if (cleanPath.startsWith("/jobs-near-me/")) {
    const rawSlug = cleanPath.replace("/jobs-near-me/", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/jobs/${targetSlug}`,
      permanent: true,
      reason: "Normalize legacy /jobs-near-me/ route to canonical /jobs/ hierarchy"
    };
  }

  // 3. Legacy Pattern: /local-jobs/:location -> /jobs/:location
  if (cleanPath.startsWith("/local-jobs/")) {
    const rawSlug = cleanPath.replace("/local-jobs/", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/jobs/${targetSlug}`,
      permanent: true,
      reason: "Normalize legacy /local-jobs/ route to canonical /jobs/ hierarchy"
    };
  }

  // 4. Legacy Pattern: /jobs-in-:location -> /jobs/:location
  if (cleanPath.startsWith("/jobs-in-")) {
    const rawSlug = cleanPath.replace("/jobs-in-", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/jobs/${targetSlug}`,
      permanent: true,
      reason: "Normalize legacy /jobs-in- prefix route to /jobs/"
    };
  }

  // 5. Legacy Pattern: /teen-jobs/:location -> /jobs/:location/teen-jobs
  if (cleanPath.startsWith("/teen-jobs/")) {
    const rawSlug = cleanPath.replace("/teen-jobs/", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/jobs/${targetSlug}/entry-level`,
      permanent: true,
      reason: "Normalize legacy teen jobs location route to hierarchical niche route"
    };
  }

  // 6. Legacy Pattern: /part-time-jobs/:location -> /jobs/:location/part-time
  if (cleanPath.startsWith("/part-time-jobs/")) {
    const rawSlug = cleanPath.replace("/part-time-jobs/", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/jobs/${targetSlug}/part-time`,
      permanent: true,
      reason: "Normalize legacy part-time jobs route to hierarchical niche route"
    };
  }

  // 7. Legacy Pattern: /local-internships/:location -> /internships/:location
  if (cleanPath.startsWith("/local-internships/")) {
    const rawSlug = cleanPath.replace("/local-internships/", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/internships/${targetSlug}`,
      permanent: true,
      reason: "Normalize legacy /local-internships/ route to /internships/"
    };
  }

  // 8. Legacy Pattern: /internships-in-:location -> /internships/:location
  if (cleanPath.startsWith("/internships-in-")) {
    const rawSlug = cleanPath.replace("/internships-in-", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/internships/${targetSlug}`,
      permanent: true,
      reason: "Normalize legacy /internships-in- route prefix"
    };
  }

  // 9. Legacy Pattern: /businesses-hiring-in-:location -> /businesses-hiring/:location
  if (cleanPath.startsWith("/businesses-hiring-in-")) {
    const rawSlug = cleanPath.replace("/businesses-hiring-in-", "");
    const resolved = resolveLocationSlug(rawSlug);
    const targetSlug = resolved.normalizedSlug || rawSlug;
    return {
      from: cleanPath,
      to: `/businesses-hiring/${targetSlug}`,
      permanent: true,
      reason: "Normalize legacy /businesses-hiring-in- route prefix"
    };
  }

  // 10. Check if a standard route contains typo slug or non-normalized state name
  const stdJobs = cleanPath.match(/^\/jobs\/([a-z0-9-]+)$/);
  if (stdJobs && stdJobs[1] !== "locations" && stdJobs[1] !== "by-location" && stdJobs[1] !== "by-category" && stdJobs[1] !== "by-work-type" && stdJobs[1] !== "for-students" && stdJobs[1] !== "no-experience") {
    const rawSlug = stdJobs[1];
    const resolved = resolveLocationSlug(rawSlug);
    if (resolved.type === "supported" && rawSlug !== resolved.normalizedSlug) {
      return {
        from: cleanPath,
        to: `/jobs/${resolved.normalizedSlug}`,
        permanent: true,
        reason: "Normalize city slug formatting or typo"
      };
    }
  }

  // 11. Normalize category + location combination routes (e.g. /jobs/restaurants/london-eng-gb -> /jobs/london-eng-gb/restaurant-jobs)
  const categoryLocMatch = cleanPath.match(/^\/jobs\/([a-z0-9-]+)\/([a-z0-9-]+)$/);
  if (categoryLocMatch) {
    const p1 = categoryLocMatch[1];
    const p2 = categoryLocMatch[2];

    const loc1 = resolveLocationSlug(p1);
    const loc2 = resolveLocationSlug(p2);
    const cat1 = getJobCategoryBySlug(p1);
    const cat2 = getJobCategoryBySlug(p2);

    // Case A: /jobs/restaurants/london-eng-gb (p1 is category alias/slug, p2 is location)
    if (cat1 && (loc2.type === "supported" || loc2.type === "state")) {
      const canonicalPath = `/jobs/${loc2.normalizedSlug}/${cat1.slug}`;
      if (cleanPath !== canonicalPath) {
        return {
          from: cleanPath,
          to: canonicalPath,
          permanent: true,
          reason: "Normalize category-location route to canonical /jobs/{location}/{category} structure"
        };
      }
    }

    // Case B: /jobs/london-eng-gb/restaurants (p1 is location, p2 is category alias e.g. restaurants -> restaurant-jobs)
    if ((loc1.type === "supported" || loc1.type === "state") && cat2) {
      const canonicalPath = `/jobs/${loc1.normalizedSlug}/${cat2.slug}`;
      if (cleanPath !== canonicalPath) {
        return {
          from: cleanPath,
          to: canonicalPath,
          permanent: true,
          reason: "Normalize category alias to canonical category slug"
        };
      }
    }
  }

  return null;
}

/**
 * Ensures NO redirect chains occur (resolves A -> B -> C down to A -> C in 1 step).
 */
export function resolveRedirectChain(pathname: string): { finalUrl: string; isRedirected: boolean; chainLength: number } {
  let currentPath = pathname;
  let chainLength = 0;
  let maxDepth = 5;

  while (chainLength < maxDepth) {
    const rule = getSeoRedirect(currentPath);
    if (!rule || rule.to === currentPath) {
      break;
    }
    currentPath = rule.to;
    chainLength++;
  }

  return {
    finalUrl: currentPath,
    isRedirected: chainLength > 0,
    chainLength
  };
}

export function getAllStaticRedirectRules(): SeoRedirectRule[] {
  return [...STATIC_REDIRECT_RULES];
}
